import React, { useState, useEffect } from 'react';
import { getLevelInfo, checkin } from '../services/level';
import './LevelCard.css';

/**
 * 레벨 카드 컴포넌트
 * - 사용자 드롭다운/프로필에서 상세 레벨 정보 표시
 * - 출석 체크 버튼 포함
 */
const LevelCard = ({ onLevelUp }) => {
  const [levelInfo, setLevelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkinMessage, setCheckinMessage] = useState(null);

  const fetchLevelInfo = async () => {
    try {
      const data = await getLevelInfo();
      setLevelInfo(data);
    } catch (err) {
      console.error('Failed to fetch level info:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevelInfo();
  }, []);

  const handleCheckin = async () => {
    if (checkinLoading) return;

    setCheckinLoading(true);
    setCheckinMessage(null);

    try {
      const result = await checkin();
      setCheckinMessage({
        type: result.expGained > 0 ? 'success' : 'info',
        text: result.message
      });

      // 레벨업 시 콜백
      if (result.levelUp && onLevelUp) {
        onLevelUp(result.newLevel);
      }

      // 레벨 정보 새로고침
      await fetchLevelInfo();

      // 3초 후 메시지 제거
      setTimeout(() => setCheckinMessage(null), 3000);
    } catch (err) {
      setCheckinMessage({
        type: 'error',
        text: '출석 체크에 실패했습니다'
      });
    } finally {
      setCheckinLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="level-card loading">
        <div className="level-card-skeleton"></div>
      </div>
    );
  }

  if (!levelInfo) {
    return null;
  }

  const { level, currentExp, nextLevelExp, totalExp, progress, title, stats } = levelInfo;

  return (
    <div className="level-card">
      {/* 레벨 헤더 */}
      <div className="level-card-header">
        <div className="level-card-icon">{title?.icon || '👀'}</div>
        <div className="level-card-info">
          <div className="level-card-level">Lv.{level}</div>
          <div className="level-card-title">{title?.name || '문파 구경꾼'}</div>
        </div>
      </div>

      {/* 경험치 바 */}
      <div className="level-card-exp">
        <div className="level-exp-header">
          <span className="level-exp-label">경험치</span>
          <span className="level-exp-value">
            {currentExp.toLocaleString()} / {nextLevelExp.toLocaleString()}
          </span>
        </div>
        <div className="level-exp-bar">
          <div
            className="level-exp-fill"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        <div className="level-exp-total">
          총 {totalExp.toLocaleString()} EXP
        </div>
      </div>

      {/* 통계 */}
      <div className="level-card-stats">
        <div className="level-stat">
          <span className="level-stat-icon">🔥</span>
          <span className="level-stat-value">{stats?.loginStreak || 0}일</span>
          <span className="level-stat-label">연속 접속</span>
        </div>
        <div className="level-stat">
          <span className="level-stat-icon">📅</span>
          <span className="level-stat-value">{stats?.totalLogins || 0}회</span>
          <span className="level-stat-label">총 접속</span>
        </div>
      </div>

      {/* 출석 체크 버튼 */}
      <button
        className={`level-checkin-btn ${checkinLoading ? 'loading' : ''}`}
        onClick={handleCheckin}
        disabled={checkinLoading}
      >
        {checkinLoading ? (
          <span className="checkin-loading">처리 중...</span>
        ) : (
          <>
            <span className="checkin-icon">✅</span>
            <span>출석 체크</span>
          </>
        )}
      </button>

      {/* 출석 메시지 */}
      {checkinMessage && (
        <div className={`level-checkin-message ${checkinMessage.type}`}>
          {checkinMessage.text}
        </div>
      )}
    </div>
  );
};

export default LevelCard;
